import React from 'react';
import { Printer, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';

const SECTION_RE = /^(Overview|Key Strengths|Growth Areas|Goals & Next Steps)\s*$/;

const scoreLabel = (avg) =>
  avg >= 4.5 ? 'Exceptional' : avg >= 3.5 ? 'Exceeds Expectations' : avg >= 2.5 ? 'Meets Expectations' : avg >= 1.5 ? 'Developing' : 'Needs Improvement';

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? 'fill-[#FFB800] text-[#FFB800]' : 'text-[#D9D2E5]'}`} />
    ))}
  </span>
);

const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function printCard(data, avg) {
  const nr = data.next_role || {};
  const ratings = data.final_ratings || [];
  const starRow = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);
  const summaryHtml = String(data.text || '').split('\n').map(line =>
    SECTION_RE.test(line.trim())
      ? `<h3 style="margin:14px 0 4px;font-size:13px;color:#2D1B4E;text-transform:uppercase;letter-spacing:1px;">${esc(line.trim())}</h3>`
      : `<p style="margin:0 0 6px;font-size:12px;line-height:1.55;color:#3A3045;">${esc(line)}</p>`).join('');
  const html = `<!DOCTYPE html><html><head><title>${esc(data.employee_name)} — Performance Report Card</title></head>
<body style="font-family:Georgia,'Times New Roman',serif;margin:0;background:#fff;color:#2D1B4E;">
<div style="max-width:720px;margin:0 auto;padding:28px;">
  <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:4px solid #6B3FA0;padding-bottom:14px;">
    <img src="${window.location.origin}/tns-logo.png" alt="The Next Step Programs" style="height:56px;" />
    <div style="text-align:right;">
      <div style="font-size:11px;letter-spacing:3px;color:#6B3FA0;font-weight:bold;">PERFORMANCE REPORT CARD</div>
      <div style="font-size:11px;color:#8E85A0;margin-top:2px;">${esc(data.period)}</div>
    </div>
  </div>
  <div style="display:flex;justify-content:space-between;align-items:center;margin:18px 0;">
    <div>
      <div style="font-size:26px;font-weight:bold;">${esc(data.employee_name)}</div>
      <div style="font-size:11px;color:#5A4D6E;margin-top:4px;">
        Supervisor: ${esc(data.supervisor_name || '—')} &nbsp;·&nbsp; Review completed: ${esc((data.completed_at || '').slice(0, 10) || '—')} &nbsp;·&nbsp; Issued by: ${esc(data.sent_by || 'TNS Programs')}
      </div>
    </div>
    ${avg != null ? `<div style="text-align:center;border:3px solid #6B3FA0;border-radius:50%;width:86px;height:86px;display:flex;flex-direction:column;justify-content:center;">
      <div style="font-size:24px;font-weight:bold;color:#6B3FA0;">${avg.toFixed(1)}</div>
      <div style="font-size:8px;letter-spacing:1px;color:#8E85A0;">OUT OF 5</div>
    </div>` : ''}
  </div>
  ${avg != null ? `<div style="background:#6B3FA0;color:#fff;text-align:center;padding:7px;border-radius:6px;font-size:13px;letter-spacing:2px;font-weight:bold;">${scoreLabel(avg).toUpperCase()}</div>` : ''}
  ${ratings.length ? `<table style="width:100%;border-collapse:collapse;margin:16px 0;">
    ${ratings.map(r => `<tr style="border-bottom:1px solid #EFEAF7;">
      <td style="padding:6px 0;font-size:12px;font-weight:bold;color:#2D1B4E;">${esc(r.category)}</td>
      <td style="padding:6px 0;font-size:12px;color:#FFB800;text-align:right;letter-spacing:2px;">${starRow(r.score)} <span style="color:#5A4D6E;font-family:Arial;">${r.score}/5</span></td>
    </tr>`).join('')}
  </table>` : ''}
  <div style="margin-top:8px;">${summaryHtml}</div>
  ${(nr.title || nr.details) ? `<div style="margin-top:18px;border:2px solid #FFB800;border-radius:8px;padding:14px;background:#FFFBEF;">
    <div style="font-size:12px;letter-spacing:2px;color:#B45309;font-weight:bold;">${esc(nr.year_label || '2027')} ROLE DETAILS</div>
    ${nr.title ? `<div style="font-size:16px;font-weight:bold;margin-top:6px;">${esc(nr.title)}</div>` : ''}
    ${nr.details ? `<p style="font-size:12px;line-height:1.55;color:#3A3045;margin:6px 0 0;white-space:pre-wrap;">${esc(nr.details)}</p>` : ''}
  </div>` : ''}
  <div style="margin-top:26px;border-top:1px solid #DED8EB;padding-top:10px;display:flex;justify-content:space-between;font-size:10px;color:#8E85A0;">
    <span>The Next Step Programs · Confidential personnel document</span>
    <span>Issued ${esc((data.sent_at || '').slice(0, 10))}</span>
  </div>
</div>
<script>window.onload = function () { window.print(); };</script>
</body></html>`;
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

export default function ReportCard({ data }) {
  const ratings = data.final_ratings || [];
  const avg = ratings.length ? ratings.reduce((a, r) => a + Number(r.score || 0), 0) / ratings.length : null;
  const nr = data.next_role || {};
  return (
    <div className="rounded-2xl border-2 border-[#6B3FA0]/30 bg-white overflow-hidden shadow-sm" data-testid={`report-card-${data.id}`}>
      <div className="bg-[#6B3FA0] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-lg p-1.5"><img src="/tns-logo.png" alt="TNS Programs" className="h-8 w-auto object-contain" /></div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#E3D7F5]">Performance Report Card</p>
            <p className="text-sm font-bold text-white">{data.employee_name} · {data.period}</p>
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={() => printCard(data, avg)} className="bg-white/15 text-white hover:bg-white/25 border-0" data-testid={`print-report-${data.id}`}>
          <Printer className="h-3.5 w-3.5 mr-1" /> Print
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#5A4D6E]">
          <span>Supervisor: <b>{data.supervisor_name || '—'}</b></span>
          <span>Completed: <b>{(data.completed_at || '').slice(0, 10) || '—'}</b></span>
          <span>Issued: <b>{(data.sent_at || '').slice(0, 10)}</b> by <b>{data.sent_by || 'TNS Programs'}</b></span>
        </div>
        {avg != null && (
          <div className="flex items-center gap-3 rounded-xl bg-[#FBF8FF] border border-[#E3D7F5] p-3" data-testid={`report-overall-${data.id}`}>
            <div className="h-14 w-14 rounded-full border-[3px] border-[#6B3FA0] flex flex-col items-center justify-center shrink-0">
              <span className="text-lg font-black text-[#6B3FA0] leading-none">{avg.toFixed(1)}</span>
              <span className="text-[7px] font-bold text-[#8E85A0]">OF 5</span>
            </div>
            <div>
              <p className="text-sm font-black text-[#2D1B4E] uppercase tracking-wide">{scoreLabel(avg)}</p>
              <p className="text-[11px] text-[#8E85A0]">Overall director rating across {ratings.length} categories</p>
            </div>
          </div>
        )}
        {ratings.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-x-5 gap-y-1.5">
            {ratings.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-[#F0ECF7] pb-1">
                <span className="text-xs font-bold text-[#2D1B4E]">{r.category}</span>
                <span className="flex items-center gap-1.5"><Stars n={Number(r.score)} /><span className="text-[10px] font-bold text-[#5A4D6E]">{r.score}/5</span></span>
              </div>
            ))}
          </div>
        )}
        <div className="text-sm text-[#3A3045] space-y-0.5">
          {String(data.text || '').split('\n').map((line, i) => (
            SECTION_RE.test(line.trim())
              ? <p key={i} className="font-black text-[#6B3FA0] uppercase tracking-wide text-xs mt-3">{line.trim()}</p>
              : <p key={i} className="whitespace-pre-wrap leading-relaxed">{line}</p>
          ))}
        </div>
        {(nr.title || nr.details) && (
          <div className="rounded-xl border-2 border-[#FFB800]/60 bg-[#FFFBEF] p-3" data-testid={`report-nextrole-${data.id}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#B45309]">{nr.year_label || '2027'} Role Details</p>
            {nr.title && <p className="text-sm font-black text-[#2D1B4E] mt-1">{nr.title}</p>}
            {nr.details && <p className="text-xs text-[#3A3045] whitespace-pre-wrap mt-1 leading-relaxed">{nr.details}</p>}
          </div>
        )}
        <p className="text-[9px] text-[#B9B2C9] pt-1 border-t border-[#F0ECF7]">The Next Step Programs · Confidential personnel document</p>
      </div>
    </div>
  );
}
